import { Clock } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'
import { cn } from '../lib/utils'

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'))

type TimePickerProps = {
  value?: string
  onChange: (value: string) => void
  /** Earliest selectable time "HH:MM" (earlier hours/minutes are disabled). */
  min?: string
  className?: string
  'data-testid'?: string
}

/** Clean 24-hour time picker: hour + minute (5-min steps) dropdowns in one control. */
const TimePicker = ({ value = '', onChange, min, className = '', 'data-testid': testId }: TimePickerProps) => {
  const [hour = '', minute = ''] = value.split(':')
  const [minHour, minMinute] = (min || '').split(':')
  const hourDisabled = (h: string) => minHour != null && minHour !== '' && h < minHour
  const minuteDisabled = (m: string) =>
    minHour != null && minHour !== '' && hour === minHour && minMinute != null && m < minMinute

  return (
    <div
      data-testid={testId}
      className={cn(
        'flex h-12 w-full items-center gap-1 rounded-2xl border border-[#1b1033]/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 transition-colors hover:border-[#ff4d8d]/40 dark:hover:border-white/20 focus-within:border-[#ff4d8d] focus-within:ring-2 focus-within:ring-[#ff4d8d]/25 motion-reduce:transition-none',
        className,
      )}
    >
      <Clock className="w-5 h-5 text-[#8b5cf6]/70 shrink-0" />
      <Select value={hour} onValueChange={(h) => onChange(`${h}:${minute || '00'}`)}>
        <SelectTrigger className="h-9 w-[58px] border-0 bg-transparent shadow-none focus:ring-0 px-2 font-medium tabular-nums">
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          {HOURS.map((h) => (
            <SelectItem key={h} value={h} disabled={hourDisabled(h)} className="tabular-nums">
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-[#4a3d66]/50 dark:text-[#c3b8dd]/60 font-semibold">:</span>
      <Select value={minute} onValueChange={(m) => onChange(`${hour || '00'}:${m}`)}>
        <SelectTrigger className="h-9 w-[58px] border-0 bg-transparent shadow-none focus:ring-0 px-2 font-medium tabular-nums">
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          {MINUTES.map((m) => (
            <SelectItem key={m} value={m} disabled={minuteDisabled(m)} className="tabular-nums">
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export default TimePicker
